import {Inject, Injectable, InjectionToken} from '@angular/core';
import {Browser} from '@capacitor/browser';
import {environment} from '../../environments/environment';

export type ExternalBrowserPlugin = Pick<typeof Browser, 'open'>;

export const EXTERNAL_BROWSER_PLUGIN = new InjectionToken<ExternalBrowserPlugin>('EXTERNAL_BROWSER_PLUGIN', {
  providedIn: 'root',
  factory: () => ({
    open: options => Browser.open(options),
  }),
});

export function resolveExternalBrowserUrl(href: string, baseUrl = environment.webAppBaseUrl): string | null {
  const normalizedHref = href.trim();
  if (!normalizedHref) {
    return null;
  }

  try {
    const url = new URL(normalizedHref, baseUrl);

    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
}

@Injectable({providedIn: 'root'})
export class ExternalBrowserService {
  private isOpening = false;

  constructor(@Inject(EXTERNAL_BROWSER_PLUGIN) private readonly browser: ExternalBrowserPlugin) {}

  async open(href: string): Promise<boolean> {
    if (this.isOpening) {
      return false;
    }

    const url = resolveExternalBrowserUrl(href);
    if (url === null) {
      return false;
    }

    this.isOpening = true;

    try {
      await this.browser.open({url});
      return true;
    } catch {
      return false;
    } finally {
      this.isOpening = false;
    }
  }
}
